import { createContext, useCallback, useContext, useEffect, useMemo, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

/**
 * Small History-API router (no dependency). Route table lives in app/routes.tsx.
 * Supports params (/u/:username), navigation state and back navigation.
 */
export interface Location {
  pathname: string;
  search: string;
  state: unknown;
  key: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let current: Location = read();
let depth = (history.state as { depth?: number } | null)?.depth ?? 0;

function read(): Location {
  const st = history.state as { user?: unknown; key?: string } | null;
  return { pathname: location.pathname, search: location.search, state: st?.user ?? null, key: st?.key ?? 'init' };
}

function emit() {
  current = read();
  listeners.forEach((l) => l());
}

window.addEventListener('popstate', (e) => {
  depth = (e.state as { depth?: number } | null)?.depth ?? 0;
  emit();
});

export function navigate(to: string, opts: { replace?: boolean; state?: unknown } = {}) {
  if (to === current.pathname + current.search && !opts.state) return;
  const key = Math.random().toString(36).slice(2);
  if (opts.replace) history.replaceState({ user: opts.state, key, depth }, '', to);
  else history.pushState({ user: opts.state, key, depth: ++depth }, '', to);
  emit();
}

/** Goes back inside the app, or to a fallback when the user landed here directly. */
export function goBack(fallback = '/') {
  if (depth > 0) history.back();
  else navigate(fallback, { replace: true });
}

export function useLocation(): Location {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}

export function useSearchParams(): URLSearchParams {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/* ---------- matching ---------- */

export interface RouteMatch<R> {
  route: R;
  params: Record<string, string>;
}

export function compile(path: string) {
  const keys: string[] = [];
  const re = new RegExp('^' + path.replace(/\//g, '\\/').replace(/:([a-zA-Z]+)/g, (_, k: string) => (keys.push(k), '([^/]+)')) + '\\/?$');
  return (pathname: string): Record<string, string> | null => {
    const m = re.exec(pathname);
    if (!m) return null;
    return Object.fromEntries(keys.map((k, i) => [k, decodeURIComponent(m[i + 1]!)]));
  };
}

const ParamsContext = createContext<Record<string, string>>({});

export function ParamsProvider({ params, children }: { params: Record<string, string>; children: ReactNode }) {
  return <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>;
}

export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  return useContext(ParamsContext) as T;
}

/* ---------- Link ---------- */

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  replace?: boolean;
  state?: unknown;
}

export function Link({ to, replace, state, onClick, ...rest }: LinkProps) {
  const handle = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(e);
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigate(to, { replace, state });
    },
    [to, replace, state, onClick],
  );
  return <a href={to} onClick={handle} {...rest} />;
}

/** Scrolls window-level scrollers to top on route change (per-page scrollers handle themselves). */
export function useScrollTopOnNavigate(ref: { current: HTMLElement | null }, key: string) {
  useEffect(() => {
    ref.current?.scrollTo({ top: 0 });
  }, [key, ref]);
}
